import type { GeneratedImage } from "@/contexts/GeneratedImagesContext";
import type { GeneratedVideo } from "@/hooks/useGeneratedVideos";
import { getResizedImageUrl, GRID_THUMBNAIL_WIDTH, GRID_THUMBNAIL_QUALITY } from "@/lib/imageUrl";

// ---------------------------------------------------------------------------
// DisplayMedia — single source of truth for all generated media in the UI
// ---------------------------------------------------------------------------

/** Shared display type for all generated media (images + videos). */
export interface DisplayMedia {
  id: string;
  type: "image" | "video";
  url: string; // Full-res URL (image_url or video_url)
  displayUrl: string; // Cloudflare-resized URL at 600px — precomputed for grids
  referenceImageUrls: string[]; // Source images used for generation
  prompt: string | null;
  model: string | null;
  resolution: string | null;
  aspectRatio: string | null;
  pipeline: string | null;
  createdAt: string;
  // Video-only
  duration?: number;
}

// ---------------------------------------------------------------------------
// Mappers: DB row types → DisplayMedia
// ---------------------------------------------------------------------------

/** Map a GeneratedImage (DB context type) → DisplayMedia. */
export function imageToDisplayMedia(img: GeneratedImage): DisplayMedia {
  return {
    id: img.id,
    type: "image",
    url: img.image_url || "",
    displayUrl: getResizedImageUrl(img.image_url || "", GRID_THUMBNAIL_WIDTH, { quality: GRID_THUMBNAIL_QUALITY }),
    referenceImageUrls: img.reference_image_url
      ? [img.reference_image_url]
      : [],
    prompt: img.prompt,
    model: img.model,
    resolution: img.resolution,
    aspectRatio: img.aspect_ratio,
    pipeline: img.pipeline,
    createdAt: img.created_at,
  };
}

/** Map a GeneratedVideo (DB hook type) → DisplayMedia. */
export function videoToDisplayMedia(video: GeneratedVideo): DisplayMedia {
  return {
    id: video.id,
    type: "video",
    url: video.video_url || "",
    displayUrl: video.thumbnail_url
      || (video.reference_image_url ? getResizedImageUrl(video.reference_image_url, GRID_THUMBNAIL_WIDTH, { quality: GRID_THUMBNAIL_QUALITY }) : ""),
    referenceImageUrls: [
      video.reference_image_url,
      video.end_frame_url,
    ].filter((url): url is string => Boolean(url)),
    prompt: video.prompt,
    model: video.model,
    resolution: video.resolution,
    aspectRatio: video.aspect_ratio,
    pipeline: video.pipeline,
    createdAt: video.created_at,
    duration: video.duration,
  };
}

// ---------------------------------------------------------------------------
// Reference image upload types (user-provided inputs, NOT generated output)
// ---------------------------------------------------------------------------

/** File-based upload reference image (ImageGenerator, ProductCatalog). */
export interface UploadedReferenceImage {
  id: string;
  url: string;
  file: File;
}

/** Base64 upload reference image (Director Chat). */
export interface ChatReferenceImage {
  id: string;
  url: string;
  base64: string;
}
